// should-commit-panel-editor-blur.ts — PSTUDIO-1433

export interface PanelEditorBlurContext {
  /** The blur carried a `relatedTarget` — focus is moving to a known element. */
  hasNextFocus: boolean;
  /** That element is inside the Expanded Cell View panel. */
  nextFocusInsidePanel: boolean;
  /**
   * The pointer press that caused THIS blur started inside the panel.
   *
   * Only the press that actually blurs the editor counts, so the caller
   * invalidates the signal on any keystroke — otherwise a keyboard commit
   * (Enter blurs programmatically, `relatedTarget` null) inherits the stale
   * press from whenever the user last clicked INTO the editor and is wrongly
   * read as an in-panel click.
   */
  pointerDownInsidePanel: boolean;
}

/**
 * PSTUDIO-1433: decide whether a blur out of the panel's editor is a real
 * commit.
 *
 * The ticket's requirement is "saves on cell commit only … clicking within or
 * around the panel should not trigger a save". The shipped guard compared the
 * blur's `relatedTarget` against the JSON editor WRAPPER, which let a click on
 * the panel's own label through: such a click focuses nothing, so
 * `relatedTarget` is null and the guard could not see that the click had
 * happened inside the panel at all. The 25 Aug manual test measured exactly one
 * `UpdateProStudioNodes` mutation from clicking the "ASSET CONTENT" label.
 *
 * Two signals settle it:
 *  - focus moving to an element inside the panel is never a commit;
 *  - a blur with no next focus is only a commit when the pointer press that
 *    caused it came from OUTSIDE the panel (clicking the grid still commits;
 *    clicking the panel's chrome does not).
 *
 * Keyboard blurs always carry a `relatedTarget`, so a stale in-panel pointerdown
 * can never suppress a genuine Tab-out commit.
 */
export function shouldCommitPanelEditorBlur({
  hasNextFocus,
  nextFocusInsidePanel,
  pointerDownInsidePanel,
}: PanelEditorBlurContext): boolean {
  if (nextFocusInsidePanel) return false;
  if (!hasNextFocus && pointerDownInsidePanel) return false;
  return true;
}
